import React, { FC, useMemo, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import moment from 'moment-timezone';

import { IState } from '../../reducers';
import { IContextReducer } from '../../reducers/contextReducer';
import { media } from '../../styleHelpers/breakpoint';
import { setExportView } from '../../actions/contextActions';
import { ExportStyle } from '../../styleHelpers/global';
import { getWopiInfo } from '../../actions/clustersFilesystemActions';
import { wopiFunc } from '../../tools/wopi';
import { useAlert } from '../../tools/hooks';
import { EAlertType } from '../../entities/IAlert';
import * as actionTypes from '../../actions/actionTypes/menuTypes';
import store from '../../store';
import { ILanguageReducer } from '../../reducers/languageReducer';
import { getMenuConfigForPathname } from './layoutMenuConfig';
import { mainLayoutPadding, topMenuHeight } from '../../tools/globalVariables';
import { selectWopiEnabled } from '../../tools/platformConfiguration';
import Cookies from './Cookies/Cookies';
import { ChunkLoader } from '../Common/ChunkLoader';

type GetWopiInfo = ReturnType<typeof getWopiInfo>;

const ContentWrapper = styled.div<{ withNavigation?: boolean, isRegisterRoute?: boolean }>`
    ${({ isRegisterRoute }) => isRegisterRoute && css`
        padding-left: 0 !important;
    `}
    ${({ withNavigation }) => withNavigation && css`
        padding-top: ${topMenuHeight}px;
    `}
    ${media.desktop`
        :not(.print-layout &) {
            padding-left: 68px;
        }
    `}
`;

const Content = styled.main<{ isRegisterRoute?: boolean; isPrintLayout?: boolean }>`
    ${props => props.isRegisterRoute ? css`
        padding: 0;
    ` : css`
        padding: ${mainLayoutPadding}px 8px;
    `}
    position: relative;
    overflow: ${props => props.isPrintLayout ? 'visible' : 'hidden'};
`;

const WopieWrapper = styled.div`
    .custom-iframe-wopi {
        width: 100vw;
        height: 99vh;
    }
`;

interface IProps {
    isTeamsRoute?: boolean;
    isHomeRoute?: boolean;
    isTeamsConfigPage?: boolean;
    isRegisterRoute?: boolean;
}

const LayoutWrapper: FC<React.PropsWithChildren<{ isPrintLayout?: boolean }>> = ({ isPrintLayout, children }) => (
    <div className={isPrintLayout ? 'print-layout' : ''}>{children}</div>
);

export const Layout: FC<React.PropsWithChildren<IProps>> = ({ children, isTeamsRoute, isHomeRoute, isTeamsConfigPage, isRegisterRoute }) => {
    const { isLoggedIn, isShadowAuth, isInExportView, translations } = useSelector<IState, IContextReducer & ILanguageReducer>(state => ({
        ...state.context,
        ...state.language
    }));
    const dispatch = useDispatch();
    const addAlert = useAlert();
    const wopiEnabled = useSelector(selectWopiEnabled);
    const paramsUrl = new URLSearchParams(globalThis.location.search);
    const { search, pathname } = useLocation();

    const [showWopi, setShowWopi] = useState<boolean>(false);
    const [wopiInfo, setWopiInfo] = useState<{ accessToken: string, accessTokenTtl: number, ownerId: string, userId: string, wopiSourceUrl: string }>(undefined);

    useEffect(() => {
        const config = getMenuConfigForPathname(pathname);
        if (config) {
            store.dispatch({ type: actionTypes.UPDATE_MENU_TITLE, ...config });
        }
    }, [pathname]);

    useEffect(() => {
        const isExport = paramsUrl.get('export') === 'true' || (wopiEnabled && !!paramsUrl.get('wopieItemId'));
        dispatch(setExportView(isExport));

        if (isExport) {
            const timeZone = paramsUrl.get('timeZone') || paramsUrl.get('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;
            moment.tz.setDefault(timeZone);
        } else {
            moment.tz.setDefault(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
    }, [search, dispatch, wopiEnabled]);

    useEffect(() => {
        if (showWopi) {
            wopiFunc();
        }
    }, [showWopi]);

    useEffect(() => {
        const queryParam = queryString.parse(globalThis.location.search);
        if (!wopiEnabled) {
            setShowWopi(false);
            return;
        }
        if (queryParam.wopieItemId) {
            dispatch<GetWopiInfo>(getWopiInfo(queryParam.wopieItemId as string, queryParam.edit === 'true')).then((res) => {
                setShowWopi(true);
                setWopiInfo(res);
            }).catch((err) => {
                addAlert(err, EAlertType.ERROR);
            });
        }
    }, [search, wopiEnabled]);

    const showNavigation = useMemo(() => !(!(isHomeRoute && !isLoggedIn) && (isTeamsConfigPage || isTeamsRoute || globalThis.location.pathname === '/register' || globalThis.location.pathname === '/registration-final-step')) && !isShadowAuth && !isInExportView, [isLoggedIn, isHomeRoute, isTeamsConfigPage, isInExportView, isTeamsRoute]);

    return (
        <>
            {isInExportView && <ExportStyle />}
            <LayoutWrapper isPrintLayout={isShadowAuth || isInExportView}>
                {showWopi ? (
                    <WopieWrapper>
                        <form id="office_form" name="office_form" target="office_frame" action={wopiInfo?.wopiSourceUrl} method="post" >
                            <input name="access_token" value={wopiInfo?.accessToken} type="hidden" />
                            <input name="access_token_ttl" value={wopiInfo?.accessTokenTtl} type="hidden" />
                            <input name="user_id" value={wopiInfo?.userId} type="hidden" />
                            <input name="owner_id" value={wopiInfo?.ownerId} type="hidden" />
                        </form>
                        <span id="frameholder" style={{ width: '500px', height: '550px' }}></span>
                    </WopieWrapper>
                ) : (
                    <ContentWrapper withNavigation={showNavigation} isRegisterRoute={globalThis.location.pathname === '/register' || globalThis.location.pathname === '/registration-final-step'}>
                        <Content isPrintLayout={isShadowAuth || isInExportView} isRegisterRoute={globalThis.location.pathname === '/register' || globalThis.location.pathname === '/registration-final-step'}>
                            {Object.keys(translations).length > 0 ? (children || <Outlet />) : <ChunkLoader />}
                        </Content>
                    </ContentWrapper>
                )}
            </LayoutWrapper>
            {!paramsUrl.get('token') &&
                <Cookies />
            }
        </>
    );
};
